'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Subscription } from '@eliadmin/core/src/api/subscription/subscription-api';
import type { ActiveSubscription } from '@eliadmin/core/src/api/customers/customers-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlDescription } from '../shared/html-description';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface SubscriptionCardClassNames {
  wrapper?: string;
  image?: string;
  title?: string;
  price?: string;
  description?: string;
  footer?: string;
}

interface SubscriptionCardProps {
  className?: SubscriptionCardClassNames;
  subscription: ActiveSubscription | Subscription;
}

export function SubscriptionCard({ className, subscription }: SubscriptionCardProps) {
  const { t } = useTranslations();

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div className={cn('flex w-full items-stretch justify-start', className?.wrapper)}>
      <Image
        src={subscription?.image?.url ?? '/images/danceana-sub.jpg'}
        alt={resolveImageAlt(subscription?.image, "Subscription card")}
        width={0}
        height={0}
        className={cn(
          'h-full w-16 object-cover object-center sm:w-24 md:w-48 lg:max-h-[244px] lg:w-32',
          className?.image,
        )}
        sizes="(max-width: 640px) 64px, 48px"
      />
      <div className="flex w-full flex-col items-start justify-start gap-6 bg-black px-6 py-8">
        <div className="flex w-full items-center justify-between">
          <h5
            className={cn(
              'text-lg font-normal text-white sm:text-xl lg:text-2xl xl:text-3xl',
              'font-heading tracking-tight',
              className?.title,
            )}
          >
            {subscription.name}
          </h5>
        </div>
        <HtmlDescription
          content={subscription.description}
          className={cn('line-clamp-3 w-full text-white/70', className?.description)}
          size="base"
        />
        <div
          className={cn(
            'flex w-full flex-wrap items-start justify-between gap-4',
            className?.footer,
          )}
        >
          <p className="text-sm font-bold text-white sm:text-base">
            {t('text3118')}: {formatDate(subscription.expires_at)}
          </p>
        </div>
      </div>
    </div>
  );
}
